// The one type-only edge out of this file. `secret-descriptions.ts` owns what
// a stored secret's description IS (and the rule for resolving one), and it
// imports nothing itself - so naming it here adds no runtime dependency and no
// cycle, where redefining the shape would let the wire and the resolver drift.
import type { SecretMeta } from '../helpers/board-agent-core/secret-descriptions';

// ── Board sync broadcast payload ──
// Multi-device sync: clients broadcast one of these after a mutation, and every
// other client connected to the same runner invalidates its query caches.
export type BoardUpdatePayload =
  | { scope: 'factiiis' }
  | { scope: 'posts'; factiiiId: number }
  | { scope: 'all' };

// ── Session types (shared with renderer) ──

/** CLI agent a space runs for AI work (ask, cards, deploy, one-shots).
 *  Per-space config: the runner's default board seeds it, boards override. */
export type AgentProvider = 'claude' | 'codex';

/** 'claude' here means the AI pipeline mode (runs the space's configured
 *  provider), as opposed to the user-driven 'bare' terminal mode. */
export type CardAIMode = 'claude' | 'bare';

/** What a CLI is doing, as the factiii plugin's hooks report it: `working` on
 *  UserPromptSubmit, `idle` on Stop, `blocked` on a permission request. */
export type AgentState = 'working' | 'idle' | 'blocked';

export type CardAISessionPhase =
  | 'starting'
  | 'research'
  | 'awaiting-input'
  | 'implement'
  | 'review'
  | 'complete'
  | 'error'
  | 'done'
  | 'bare-active'
  | 'bare-review';

export type CardAITodo = {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
};

// ── Agent skills (SKILL.md) ──

/** State of the one symlink Claude Code needs: `.claude/skills` must point at
 *  `.agents/skills`. `blocked` means a real directory sits there, so the skills
 *  inside it must move before a link can replace it. */
export type SkillLinkState = 'linked' | 'missing' | 'blocked';

/** `global` = the runner host's own home dir, shared by every space it drives.
 *  `repo` = committed to the space's repo, changeable only through a merge. */
export type SkillScope = 'global' | 'repo';

/** A repo skill's state relative to the default branch. Global skills are
 *  always `live`: writing one applies it immediately. */
export type SkillStatus =
  | 'live' // on the default branch: what the agents actually load
  | 'pending-new' // exists only on an unmerged skill/* branch
  | 'pending-edit'; // on both, and they differ

export type SkillSummary = {
  name: string;
  scope: SkillScope;
  description: string;
  /** Absolute for global skills, repo-relative for repo skills. */
  dir: string;
  status: SkillStatus;
  /** The unmerged branch carrying it, when status is not `live`. */
  pendingBranch: string;
  /** Frontmatter problems. The skill still opens so it can be fixed. */
  errors: string[];
};

export type CardAILogEntry = {
  time: number;
  type: 'system' | 'text' | 'tool' | 'error' | 'text-append' | 'thinking';
  content: string;
  /** On a Board AI log line: the conversation it streams into. Several can
   *  answer at once on the one board log channel. Absent from a runner older
   *  than conversations, and from every other log. */
  conversationId?: string;
};

export type CardAISessionSummary = {
  taskId: string;
  postId: string;
  phase: CardAISessionPhase | null;
  mode: CardAIMode;
  step: 1 | 2 | 3;
  diff: string;
  changedFiles: string[];
  commitMessage: string;
  previewUrl: string;
  previewRunning: boolean;
  reproSteps: string;
  pendingQuestion: string;
  error: string;
  startedAt: number;
  /** Absent when the plugin is not installed or has not fired yet, in which
   *  case the dot falls back to "alive". */
  agentState?: AgentState;
  targetBranch: string;
  taskTitle: string;
  todos: CardAITodo[];
  /**
   * The provider CLI's own conversation id (claude session UUID / codex
   * thread id), threaded across the research→implement→review spawns. Each
   * spawn is headless `-p` print mode and exits, so there's no live process
   * to attach to — but this id resumes the thread interactively from a shell
   * in the same workspace and cwd. Empty until the first spawn reports one.
   */
  agentSessionId: string;
  /** The CLI that ran this session (per-job; may differ from the space
   *  default). A terminal must resume with THIS provider, not config's. */
  provider: AgentProvider;
};

export type CardAISessionFull = CardAISessionSummary & {
  logs: CardAILogEntry[];
};

// ── Runner activity (everything the runner does that ISN'T a card session) ──

/**
 * Long-running space-level work: Ask Board, deploys, setup, indexing, sign-ins.
 * Sessions are card-keyed and open a terminal; these are not, and each belongs
 * to its own modal — so they ride their own channel rather than being bent
 * into `CardAISessionSummary`.
 */
export type RunnerActivityKind =
  | 'ask'
  | 'deploy-run'
  | 'deploy-skill'
  | 'deploy-env'
  | 'setup'
  | 'onedrive-index'
  | 'secrets-auth'
  | 'signin';

/** One command waiting on the owner's store password, as the global
 *  authorization modal renders it. No secret value ever appears here: a
 *  command string, the key NAMES it will receive, and which session asked. */
export type PendingSecretsAuth = {
  /** Identifies this prompt across the wire, so a stale client cannot answer
   *  a request that has already been replaced by the next one. */
  id: string;
  command: string;
  /** The keys this command will receive, and nothing else in the store. */
  keys: string[];
  /** Which session asked - "Deploy run", "Terminal · <card>". Never blank:
   *  an unattributed request is refused before it ever becomes a prompt. */
  origin: string;
  /** A previous attempt's failure (wrong password), shown on the re-prompt. */
  error: string;
  requestedAt: number;
  /** Who asked, server-vouched (a job call or an inbox write on a shared
   *  runner). Absent for a free command from the owner's own session. */
  caller?: { userId: string; name: string };
  /** The job this spawn belongs to. Only a job may be covered by an unlock
   *  window, so the prompt offers a duration only when this is set. */
  job?: string;
  /** Set when approving writes a secret from the inbox rather than running a
   *  command: which key, env value or file blob, and whether it replaces one. */
  inbox?: { id: string; key: string; kind: 'env' | 'file'; replaces: boolean };
};

/** Who opened the current unlock window and until when (ISO). */
export type SecretsUnlockWindow = {
  until: string;
  byUserId: string;
  byName: string;
};

/** One line of the runner's append-only audit log
 *  (`~/.factiii-runner/<space>/audit.jsonl`). Names only - never a value, the
 *  password, or a decrypted file path. `prev` is the sha256 of the line before
 *  it, so an edited or deleted line breaks the chain. */
export type AuditEntry = {
  ts: string;
  actor: { userId: string; name: string };
  via: 'api' | 'data-channel' | 'owner-terminal';
  action:
    | 'job'
    | 'job-end'
    | 'job-cancel'
    /** A free `factiii-secrets run` from a session on the runner. */
    | 'run'
    | 'unlock'
    | 'lock'
    | 'secret-set'
    | 'secret-unset'
    | 'password-rotate'
    | 'shared-change'
    /** A wrong runner password, or one refused while the guess limit's
     *  backoff runs (password-throttle.ts). `actor` is who typed it. */
    | 'auth-failed'
    | 'denied';
  job: string | null;
  /** Key names (and file-blob names) only. */
  keys: string[];
  /** The resolved command from the job definition, never its output. */
  command: string | null;
  exitCode: number | null;
  durationMs: number | null;
  /** Who opened the window this ran in, when it ran inside one. */
  unlockedBy: string | null;
  /** Free-form, value-free context (e.g. "inbox", "shared=true"). */
  detail?: string;
  prev: string;
};

export type RunnerJobInfo = {
  name: string;
  description: string;
  keys: string[];
  files: string[];
  /** Param name -> the regex its value must match. */
  params: Record<string, string>;
};

export type RunnerJobState =
  | 'queued'
  | 'waiting-auth'
  | 'running'
  // Server only: the runner got the call but did not confirm it in time, so
  // it may be running. Its later reports still move the row, cancel still
  // reaches it, and nothing sends it again on its own.
  | 'unknown'
  | 'done'
  | 'denied'
  | 'failed'
  | 'cancelled';

export type SecretsInboxItem = {
  id: string;
  key: string;
  kind: 'env' | 'file';
  replaces: boolean;
  byUserId: string;
  byName: string;
  at: string;
};

/** 'working' spins; the other two are stationary and want attention. */
export type RunnerActivityState = 'working' | 'awaiting-input' | 'error';

export type RunnerActivity = {
  /** Stable per space+kind (+ discriminator for sign-ins, which can overlap),
   *  so an update replaces its row instead of stacking another one. */
  id: string;
  kind: RunnerActivityKind;
  /** null means finished — the client drops the row. */
  state: RunnerActivityState | null;
  /** The row's one line, e.g. "Ask Board" or "Deploying". */
  label: string;
  /** Current step, shown in the tooltip, e.g. "cloning repo". */
  detail: string;
  startedAt: number;
};

// ── IPC action map (mirrors desktop/src/types.ts) ──

/**
 * How the host that stores this space's credentials is doing at protecting
 * them. There is no unencrypted mode: a host that cannot encrypt reports
 * `ready: false` and refuses to read or write secrets at all, carrying the
 * problem and its remedy so the UI can print them verbatim.
 */
export type SecureStoreStatus = {
  backend: 'runner-keychain' | 'electron-safe-storage';
  /** Which OS facility holds the machine key. Informational, drives UI copy. */
  keyStore: 'macos-keychain' | 'libsecret' | 'dpapi' | 'electron-safe-storage';
  /** Reads and writes of credentials will succeed right now. */
  ready: boolean;
  /** Whether this backend offers the optional owner password (runner only). */
  passwordSupported: boolean;
  /** Whether the owner added that second factor on top of the machine key. */
  passwordEnabled: boolean;
  /** Whether the key is available in the host process right now. */
  unlocked: boolean;
  /** Missing binary, when that's the cause. Empty otherwise. */
  missingTool: string;
  /** One-line problem statement. Empty when ready. */
  problem: string;
  /** Copy-pasteable remedy, possibly multi-line. Empty when ready. */
  fix: string;
};

export type ConfigResponse = {
  repoUrl: string;
  mainBranch: string;
  gitName: string;
  gitEmail: string;
  // The CLI agent this space runs. Older runners omit this (claude).
  provider: AgentProvider;
  // True when the space has persisted `claude /login` credentials on the
  // runner - the only Claude auth. Older runners omit this.
  claudeLoggedIn: boolean;
  // True when the space has persisted `codex login` credentials (auth.json)
  // on the runner. Older runners omit this.
  codexLoggedIn: boolean;
  hasGithubToken: boolean;
  githubTokenHint: string;
  // The credential itself is never surfaced - only whether it's connected.
  onedrive: { connected: boolean };
  // Whether this host can encrypt connection tokens at rest right now. False
  // means it reported back that it CANNOT - a machine with no usable keychain,
  // or (on newer runners) one that is locked behind the owner password. Such a
  // runner stores nothing rather than falling back to plaintext.
  //
  // Undefined is a THIRD state, not a synonym for false: a runner too old to
  // know about this field omits it. Such a runner is certainly not encrypting,
  // but the reason is "old build", not "no keychain" - and the UI must not
  // assert a cause it can't observe. Keep the distinction; don't `?? false`.
  tokensEncrypted?: boolean;
  // The detail behind tokensEncrypted: which key store, whether the optional
  // password layer is on, whether it's unlocked, and the exact fix when it is
  // not usable. Omitted by runners that predate the secure-store rework.
  secureStore?: SecureStoreStatus;
};

/** A folder the owner picked to index. The selection is the whole index scope:
 *  the host never walks anything outside it. */
export type OneDriveSelectedFolder = {
  itemId: string;
  name: string;
  /** Full OneDrive path of the folder ('/Documents/Projects'). */
  filePath: string;
};

/** One row in the folder picker. `covered` means a selected ancestor already
 *  indexes this folder, so it offers no Sync action of its own. */
export type OneDriveBrowsedFolder = {
  id: string;
  name: string;
  filePath: string;
  childCount: number;
  selected: boolean;
  covered: boolean;
  /** Path of the selected ancestor, when covered. */
  coveredBy?: string;
};

export type SetConfigPayload = {
  repoUrl?: string;
  mainBranch?: string;
  gitName?: string;
  gitEmail?: string;
  githubToken?: string;
  provider?: AgentProvider;
};

/** Result of one device-code poll. */
export type OneDrivePollStatus =
  | 'pending'
  | 'slow_down'
  | 'complete'
  | 'expired'
  | 'declined'
  | 'error';

/** One path in a bare workspace's git status, tagged with the porcelain letter
 *  that put it there: 'M' modified, 'A' added, 'D' deleted, 'R' renamed, 'U'
 *  unmerged. The same path can appear staged AND unstaged - git tracks the
 *  index and the working tree separately, and so does the panel. */
export type BareGitFileStatus = {
  path: string;
  code: string;
};

/** One commit on the workspace's branch, for the panel's history list. */
export type BareGitCommit = {
  sha: string;
  shortSha: string;
  author: string;
  /** ISO-8601 author date. */
  date: string;
  /** Git's own phrasing ("3 hours ago"), so the panel needs no clock. */
  relative: string;
  subject: string;
  /** Not reachable from any remote branch: committed here, never pushed. */
  local: boolean;
};

/** What the scratchpad panel does with one file. Derived from the extension
 *  alone (`helpers/scratchpad.ts`), so an agent picks a renderer by naming the
 *  file rather than by writing a manifest beside it. */
export type ScratchpadKind =
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'html'
  | 'markdown'
  | 'link'
  | 'text';

/** One file on a space's scratchpad. Contents are fetched per item, so a panel
 *  listing a 40MB video costs one small call until something asks to play it. */
export type ScratchpadItem = {
  /** Bare filename. The scratchpad is flat: no separators, ever. */
  name: string;
  kind: ScratchpadKind;
  size: number;
  /** Epoch ms. The panel orders newest first. */
  modifiedAt: number;
  /** Past SCRATCHPAD_MAX_BYTES: listed, never inlined. The panel says so
   *  rather than hanging on a read the transport would take minutes to chunk. */
  tooLarge: boolean;
};

/** A running simulator. Only booted devices are listed — a shutdown one has no
 *  display to attach to. See .specs/simulator-panel.md. */
export type SimulatorDevice = {
  udid: string;
  name: string;
  /** Trimmed for display: "iOS 26 4", not the full reverse-DNS runtime id. */
  runtime: string;
};

/**
 * One input event for a simulator.
 *
 * Touch carries 0..1 ratios, not points: the HID layer normalises against the
 * screen itself, so neither end needs to know the device's size. `move` is what
 * separates this from a canned swipe — the simulator tracks the pointer as it
 * travels, which is the difference between scrolling and watching a list jump
 * once on release.
 */
export type SimulatorCommand =
  | { type: 'touch'; phase: 'down' | 'move' | 'up'; x: number; y: number }
  | { type: 'tap'; x: number; y: number }
  | { type: 'text'; text: string }
  /** One key going down or up, as a USB HID usage code. What a hardware
   *  keyboard sends: the device supplies shift, repeat and shortcuts itself. */
  | { type: 'key'; code: number; down: boolean }
  | { type: 'button'; button: 'HOME' | 'LOCK' | 'SIRI' | 'SIDE' | 'APPLE_PAY' }
  /** Face ID / Touch ID. Not HID — a Darwin notification, the same one
   *  Simulator.app's Features menu posts. A non-match is the more useful test:
   *  it is the path an app's error handling actually has to cover. */
  | { type: 'biometric'; kind: 'face' | 'touch'; match: boolean };

/** A command plus the device it is for. Split from `SimulatorCommand` because
 *  `Omit` over a union collapses it, and the panel builds commands without
 *  knowing the udid. */
export type SimulatorInput = SimulatorCommand & { udid: string };

/** The owner's two member switches on a runner (Runner Settings). */
export type RunnerAccessState = {
  apiAccess: boolean;
  shareTerminals: boolean;
};

/** The image formats an AI question can carry. A GIF is sent as a still. */
export const ASK_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif'
] as const;
export type AskImageType = (typeof ASK_IMAGE_TYPES)[number];

/** An image attached to an Ask Board question: base64 in the request itself.
 *  It is context for one turn, never an upload - nothing reaches S3/R2, and
 *  the runner keeps no copy once the turn ends. */
export type AskImage = { mediaType: AskImageType; data: string };

export const ASK_IMAGE_MAX_COUNT = 4;
/** Per image, decoded. Clients downscale well below this before sending. */
export const ASK_IMAGE_MAX_BYTES = 5_000_000;

/** One turn of the Ask Board conversation as the runner keeps it. */
export type BoardSessionMessage = {
  role: 'user' | 'assistant';
  content: string;
  /** How many images rode with a user turn. The images are not kept. */
  imageCount?: number;
};

/** One Board AI conversation in the session list. */
export type BoardConversationSummary = {
  id: string;
  /** The first question, until the user renames it. */
  title: string;
  /** "List > Card title" when the conversation is about one card, else ''. */
  topic: string;
  /** The last thing the user asked. */
  preview: string;
  provider: AgentProvider | null;
  /** Set aside: out of the open tabs, still resumable from the history. */
  archived: boolean;
  /** A turn is in flight on the runner right now. */
  busy: boolean;
  messageCount: number;
  createdAt: number;
  updatedAt: number;
};

export type BoardAgentActionMap = {
  getConfig: { payload: void; result: ConfigResponse };
  setConfig: { payload: SetConfigPayload; result: { success: boolean } };
  clearToken: {
    payload: { key: 'githubToken' };
    result: { success: boolean };
  };
  ask: {
    payload: {
      question: string;
      boardContext?: string;
      componentSchema?: string;
      /** Provider for a NEW conversation. Locked once the conversation starts
       *  (a claude session and a codex thread aren't interchangeable). */
      provider?: AgentProvider;
      /** Screenshots and images for this turn, piped straight to the model. */
      images?: AskImage[];
      /** Which conversation this turn belongs to; the first question with a
       *  new id starts it. A client older than conversations sends none and
       *  gets the most recently used one. */
      conversationId?: string;
    };
    result: string;
  };
  /** From a client older than conversations: set the current one aside. It
   *  stays in the history; nothing is deleted. */
  resetBoardSession: { payload: void; result: void };
  /** One persisted Ask Board conversation, for hydrating the panel on open. No
   *  id means the most recently used one. */
  boardSession: {
    payload: { conversationId?: string } | void;
    result: {
      messages: BoardSessionMessage[];
      /** Absent from a runner older than conversations. */
      conversationId?: string;
      busy?: boolean;
    };
  };
  /** Every Ask Board conversation of the space, most recently used first. */
  boardSessions: {
    payload: void;
    result: { conversations: BoardConversationSummary[] };
  };
  /** Rename, label with a topic, or set aside / bring back a conversation. */
  boardSessionUpdate: {
    payload: {
      conversationId: string;
      title?: string;
      topic?: string;
      archived?: boolean;
    };
    result: void;
  };
  /** Delete a conversation for good, stopping any turn in flight. */
  boardSessionDelete: { payload: { conversationId: string }; result: void };
  /** Spotlight-style OneDrive index status + persistent size for the UI. */
  onedriveIndexStatus: {
    payload: void;
    result: {
      status: 'indexing' | 'idle' | 'error';
      done: number;
      error?: string;
      files: number;
      chunks: number;
      images: number;
      /** How many folders the owner picked to index. */
      folders: number;
      selected: OneDriveSelectedFolder[];
    };
  };
  /** Browse one level of the drive for the folder picker. No `parentId` browses
   *  the drive root. */
  onedriveBrowseFolders: {
    payload: { parentId?: string };
    result: { folders: OneDriveBrowsedFolder[]; fileCount: number };
  };
  /** Add a folder to the index selection; the host indexes it right away and
   *  keeps it in sync from then on. */
  onedriveSelectFolder: {
    payload: OneDriveSelectedFolder;
    result: { success: boolean };
  };
  /** Remove a folder from the selection, deleting everything indexed under it. */
  onedriveUnselectFolder: {
    payload: { itemId: string };
    result: { success: boolean };
  };
  /** Semantic search over the runner's OneDrive index (docs + images). */
  onedriveSearch: {
    payload: { query: string; k?: number };
    result: {
      itemId: string;
      name: string;
      filePath: string;
      kind: 'text' | 'image';
      seq?: number;
      distance: number;
    }[];
  };
  cardExecute: {
    payload: {
      postId: string;
      prompt: string;
      baseBranch: string;
      targetBranch: string;
      taskTitle: string;
      effort: string;
      mode: CardAIMode;
      // Per-job agent provider, defaulting to the space config when absent.
      provider?: AgentProvider;
    };
    result: { taskId: string; postId: string };
  };
  // On-demand live dev-server preview for a completed claude session.
  cardStartPreview: {
    payload: { postId: string };
    result: { previewUrl: string };
  };
  // One-shot on the runner host: combine the selected cards' text into the
  // merged card's title/content. Runs before posts.merge creates the card.
  cardMergeContent: {
    payload: { cards: { title: string; content: string }[] };
    result: { title: string; content: string };
  };
  // Card merge follow-up: take the diffs from the source cards' completed
  // sessions and have Claude reconcile them in a fresh session on the primary
  // card, ending in a normal 'complete' phase (diff review + push).
  cardMergeSessions: {
    payload: {
      primaryPostId: string;
      // Cards whose completed sessions supply the diffs; may include the primary.
      sourcePostIds: string[];
      taskTitle: string;
    };
    result: { taskId: string; postId: string };
  };
  cardSessions: { payload: void; result: Record<string, CardAISessionSummary> };
  cardSession: {
    payload: { postId: string };
    result: CardAISessionFull | null;
  };
  cardDismiss: { payload: { postId: string }; result: void };
  setupStatus: {
    payload: void;
    result: { phase: 'idle' | 'running' | 'ready' };
  };
  ensureSetup: { payload: void; result: { ready: boolean } };
  cardRemoteBranches: { payload: void; result: string[] };
  /** Worktree names already in use, so the terminal launcher can refuse a
   *  duplicate name up front instead of failing the launch. */
  bareWorkNames: { payload: void; result: string[] };
  // Deploy button entry: does the default branch carry the deploy contract
  // (.agents/skills/deploy/SKILL.md + required-variables.json)? Missing
  // pieces route the UI to the skill creator. See .specs/deploy.md.
  deployStatus: {
    payload: void;
    result: {
      hasSkill: boolean;
      hasVariables: boolean;
      hasChecker: boolean;
      branch: string;
      // Newest unmerged deploy-skill* branch with the full contract; the
      // creator offers to resume it instead of regenerating.
      pendingBranch: string;
      variables: {
        key: string;
        title: string;
        description: string;
        category: string;
        sensitive: boolean;
      }[];
    };
  };
  // The Deploy modal's release picker: what is on origin that could ship,
  // newest commit first. The default branch is excluded - it IS production,
  // so a release never starts there. `ahead` = commits the branch would bring
  // to it (0 = already merged), `updated` = its last commit, epoch SECONDS.
  deployBranches: {
    payload: void;
    result: { name: string; ahead: number; updated: number; subject: string }[];
  };
  // Runner secrets store (the AI-safe contract: values are write-only,
  // the wire carries key NAMES and set-status only). NO caching: every
  // operation takes the password and decrypts for that one call.
  deploySecretsStatus: {
    payload: void;
    result: { initialized: boolean };
  };
  deploySecretsInit: {
    payload: { password: string };
    result: { success: boolean };
  };
  // Password check + key names in one call (the settings view's entry).
  // `meta` is the descriptions sidecar (secrets.meta.json): what each key is
  // FOR, never what it is. It rides this call rather than one of its own
  // because the panel wants both at once, and it needs no password of its own.
  deploySecretsList: {
    payload: { password: string };
    result: {
      keys: string[];
      meta: Record<string, SecretMeta>;
      /** File-secret (sops blob) NAMES. Older runners omit it. */
      files?: string[];
    };
  };
  deploySecretsSet: {
    payload: { password: string; key: string; value: string };
    result: { success: boolean };
  };
  // Describe a key. No password: this writes no secret material, and sops
  // already leaves key names in cleartext. Omitted fields keep their current
  // text, so saving a title cannot blank a description.
  deploySecretsSetMeta: {
    payload: { key: string } & Partial<SecretMeta>;
    result: { success: boolean };
  };
  deploySecretsUnset: {
    payload: { password: string; key: string };
    result: { success: boolean };
  };
  /** Store a file secret (sops blob at secrets/files/<NAME>.enc), replacing
   *  any with that name. Owner only. `name` is env-style (it becomes the
   *  variable holding the temp file's path); the file is at most 5 MB. */
  deploySecretsSetFile: {
    payload: { password: string; name: string; contentBase64: string };
    result: { success: boolean };
  };
  /** Remove a file secret. Owner only. */
  deploySecretsUnsetFile: {
    payload: { password: string; name: string };
    result: { success: boolean };
  };
  // Skill creator: the AI analyzes the repo + the owner's described process
  // and drafts SKILL.md + required-variables.json into the host workspace,
  // UNCOMMITTED. The owner previews the skill and its requested secrets,
  // revises, then explicitly approves the push. Long-running; streams via the
  // board log.
  deployCreateSkill: {
    // `provider` is per-run, defaulting to the space config when absent - the
    // same shape cardExecute uses. Every deploy action that spawns an agent
    // takes it, so one pick in the modal covers check/fix, create and run.
    payload: { instructions: string; provider?: AgentProvider };
    result: {
      skill: string;
      // The generated check-environment.sh, for the review UI.
      checker: string;
      variables: {
        key: string;
        title: string;
        description: string;
        // AI-chosen grouping: server, client, database, deploy access...
        category: string;
        sensitive: boolean;
      }[];
    };
  };
  // ── Agent skills (SKILL.md), space-scoped like the deploy family ──
  // `.agents/skills` is the only source of truth; Claude Code reaches it
  // through a `.claude/skills/<name>` symlink, which `claudeLink` tracks.
  // Two scopes, both served by host-skills.ts:
  //   global - the host's own ~/.agents/skills. Written directly; every space
  //            this runner drives sees it.
  //   repo   - committed skills, read at a ref. Edits become a pushed
  //            `skill/*` branch and stay pending until a human merges it.
  skillsList: {
    payload: void;
    result: {
      global: SkillSummary[];
      repo: SkillSummary[];
      /** Whether `.claude/skills` links to `.agents/skills` in each scope.
       *  Anything but `linked` blocks the modal until the user migrates. */
      globalLink: SkillLinkState;
      repoLink: SkillLinkState;
      repoBranch: string;
      // False when the space has no repo configured, so the UI explains the
      // gap rather than showing an empty list that reads as "no skills".
      repoAvailable: boolean;
      // `skill/migrate` while a migration is pushed and unmerged, else ''. The
      // gate stays up until the merge lands, so the UI needs this to explain
      // the wait instead of offering to propose the same branch again.
      repoMigrationBranch: string;
      // Skills that landed on the default branch after that migration was
      // pushed, so its branch no longer moves all of them. Normally empty.
      repoMigrationMissing: string[];
    };
  };
  skillRead: {
    payload: { scope: SkillScope; name: string };
    result: {
      name: string;
      scope: SkillScope;
      content: string;
      errors: string[];
      // Other files in the skill dir. Listed for context; not editable in v1.
      bundleFiles: string[];
      status: SkillStatus;
      pendingBranch: string;
    };
  };
  skillWriteGlobal: {
    payload: { name: string; content: string };
    result: void;
  };
  skillDeleteGlobal: {
    payload: { name: string };
    result: void;
  };
  /** Push the edit to `skill/<name>` and stop. Merging on GitHub is the
   *  approval gate, as with approveDeploySkill and cardFinalize. */
  skillProposeRepo: {
    payload: { name: string; content: string };
    result: { branch: string };
  };
  /** Move skills out of `.claude/skills` and `.codex/skills` into
   *  `.agents/skills`, then replace `.claude/skills` with one symlink. Global
   *  applies at once; repo returns the review branch to merge. */
  skillMigrate: {
    payload: { scope: SkillScope };
    // `pushed` is false when the branch was already open and unchanged, so the
    // UI can say "already waiting" instead of claiming a fresh push.
    result: { branch: string; moved: string[]; pushed: boolean };
  };

  // ── Deploy run (executing the merged skill on the host) ──
  // Start returns immediately; progress rides the 'deploy-run' event +
  // deployRunState. Gates: answers resume the agent session; authorization
  // carries the store password RUNNER-DIRECT (never through the session).
  deployRunStart: {
    payload: {
      provider?: AgentProvider;
      /** Release branch picked in the modal. Omitted (older clients, or no
       *  pick) leaves the skill to ask for it during the run. */
      branch?: string;
    };
    result: { started: boolean };
  };
  deployRunState: {
    payload: void;
    result: {
      /** The CLI this run is actually using. Reported rather than inferred:
       *  a reopened modal would otherwise show the space default while the
       *  run is on the other one. Older runners omit it. */
      provider: AgentProvider;
      phase:
        | 'idle'
        | 'running'
        | 'awaiting-input'
        | 'awaiting-auth'
        | 'success'
        | 'failed';
      todos: CardAITodo[];
      logs: CardAILogEntry[];
      question: string;
      pendingCommand: string;
      /** The keys the pending command will receive - the scope the owner is
       *  actually authorizing. Older runners omit it. */
      pendingKeys: string[];
      error: string;
      // Agent-written end-of-run report (text after DEPLOY_COMPLETE/
      // DEPLOY_FAILED): what shipped, what was skipped, owner follow-ups.
      summary: string;
      startedAt: number;
      finishedAt: number;
    };
  };
  deployRunAnswer: {
    payload: { answer: string };
    result: { success: boolean };
  };
  /** Answer a sudo request from Initialize. An empty password denies, and the
   *  install fails with a message instead of hanging. Runner-direct: the
   *  password never reaches the server. */
  setupAuthorize: {
    payload: { password: string };
    result: { success: boolean };
  };
  /** Kept for clients older than the global authorization modal: both answer
   *  whatever the space's gate is currently waiting on. New clients send
   *  `secretsAuthAnswer`, which names the request it is answering. */
  deployRunAuthorize: {
    payload: { password: string };
    result: { success: boolean };
  };
  deployRunDeny: { payload: void; result: { success: boolean } };
  /** What the global authorization modal renders: the one command waiting on
   *  the owner's store password, wherever in the space it was run from.
   *  `pending: null` means nothing is waiting - the modal closes. */
  secretsAuthState: {
    payload: void;
    result: {
      pending: PendingSecretsAuth | null;
      /** The open unlock window (jobs only), or null when locked. */
      unlocked: SecretsUnlockWindow | null;
    };
  };
  /** Answer a specific pending request. Omitting `password` denies it. The id
   *  must match the request on screen: a wrong-password re-prompt replaces it,
   *  and a stale answer must not resolve its successor. Runner-direct - the
   *  password never reaches the server. */
  secretsAuthAnswer: {
    /** `unlockFor` (minutes, default 120, max 720) opens an unlock window
     *  when the request is a job: later job spawns run without a prompt until
     *  it ends. 0 or absent on a free command = this one spawn only. */
    payload: { id: string; password?: string; unlockFor?: number };
    result: { success: boolean };
  };
  /** End the unlock window now: the runner wipes the age key from memory. */
  secretsLock: {
    payload: Record<string, never>;
    result: { success: boolean };
  };
  /** Re-wrap the age key under a new password (owner only). No secret value
   *  changes; the old password stops working. */
  secretsChangePassword: {
    payload: { oldPassword: string; newPassword: string };
    result: { success: boolean };
  };
  /** What this runner gives board members (runner-access.ts): "Allow API
   *  access" (jobs, the secrets gate, encrypted secret updates) and "Share
   *  terminals" (terminals, card AI, the simulator, file writes). Any channel
   *  may read it (a member's app hides what it cannot use); only the owner
   *  sets it. A set changes only the fields it names. */
  runnerAccessGet: {
    payload: void;
    result: RunnerAccessState;
  };
  runnerAccessSet: {
    payload: Partial<RunnerAccessState>;
    result: RunnerAccessState;
  };
  /** The jobs the default branch declares (.agents/skills/deploy/jobs.json). */
  jobsList: {
    payload: void;
    result: { jobs: RunnerJobInfo[] };
  };
  /** Call a job by name. Never a command: unknown jobs and params that fail
   *  their regex are refused. */
  jobsCall: {
    payload: { job: string; params?: Record<string, string> };
    result: { callId: string };
  };
  /** Cancel a call: a queued or gate-waiting one leaves the queue, a running
   *  one gets SIGTERM (SIGKILL after 10 s). Only the actor who started it or
   *  the runner owner may. `cancelled: false` = it had already ended. */
  jobCancel: {
    payload: { callId: string };
    result: { cancelled: boolean };
  };
  /** Calls that have not ended (queued, waiting-auth, running), from every
   *  path. The owner gets all of them; a member channel only its own. */
  jobsActive: {
    payload: void;
    result: {
      calls: {
        callId: string;
        job: string;
        state: RunnerJobState;
        actor: { userId: string; name: string };
        startedAt: string;
      }[];
    };
  };
  /** A call's state plus its redacted log after `afterSeq`. */
  jobStatus: {
    payload: { callId: string; afterSeq?: number };
    result: {
      state: RunnerJobState;
      exitCode: number | null;
      chunks: { seq: number; text: string }[];
    };
  };
  /** Read the audit log, newest first (owner only). `chainIntact` covers the
   *  whole file, not only the page returned. */
  auditRead: {
    payload: {
      limit?: number;
      beforeTs?: string;
      actorUserId?: string;
      job?: string;
      key?: string;
    };
    result: {
      entries: AuditEntry[];
      chainIntact: boolean;
      firstBrokenTs: string | null;
    };
  };
  /** Encrypted secret updates parked on the runner, waiting for approval. */
  secretsInboxList: {
    payload: void;
    result: { items: SecretsInboxItem[] };
  };
  /** Approve (needs the password) or deny an inbox item. */
  secretsInboxAnswer: {
    payload: {
      id: string;
      password?: string;
      approve: boolean;
      unlockFor?: number;
    };
    result: { success: boolean };
  };
  deployRunCancel: { payload: void; result: { success: boolean } };
  deployRunDismiss: { payload: void; result: { success: boolean } };
  // Run the merged check-environment.sh: a read-only, secret-free preflight
  // proving this machine can run the whole flow ("good to prod push").
  deployCheckEnvironment: {
    payload: void;
    result: {
      ok: boolean;
      checks: { ok: boolean; label: string; detail: string }[];
      output: string;
    };
  };
  // The check failed: AI installs/configures what the failed checks need
  // (env setup only; no repo writes, no secrets), then re-checks.
  // Long-running; streams via the board log.
  deployFixEnvironment: {
    payload: { provider?: AgentProvider };
    result: BoardAgentActionMap['deployCheckEnvironment']['result'];
  };
  // In-flight/settled check+fix state (engine memory), so a reopened modal
  // reattaches instead of losing a running fix.
  deployEnvState: {
    payload: void;
    result: {
      phase: 'idle' | 'checking' | 'fixing' | 'done';
      logs: CardAILogEntry[];
      result: BoardAgentActionMap['deployCheckEnvironment']['result'] | null;
      error: string;
    };
  };
  deployReviseSkill: {
    payload: { feedback: string; provider?: AgentProvider };
    result: BoardAgentActionMap['deployCreateSkill']['result'];
  };
  // Check out a pushed deploy-skill branch (committed as-is) into review;
  // approved revisions become their own commit on that branch.
  deployLoadSkillBranch: {
    payload: { branch: string };
    result: BoardAgentActionMap['deployCreateSkill']['result'];
  };
  // In-flight creator state (engine memory), so a reopened modal reattaches:
  // phase + buffered logs + the last drafted result.
  deployCreatorState: {
    payload: void;
    result: {
      phase: 'idle' | 'working' | 'review';
      logs: CardAILogEntry[];
      draft: BoardAgentActionMap['deployCreateSkill']['result'] | null;
      error: string;
    };
  };
  // Push the previewed draft to a branch; merging it arms the Deploy button.
  deployApproveSkill: {
    payload: void;
    result: { branch: string };
  };
  deployDiscardSkill: {
    payload: void;
    result: { success: boolean };
  };
  // OneDrive host-run device-code auth: frontend passes the public client_id,
  // the host polls Microsoft and pushes an `onedrive-auth` event on completion.
  onedriveStartDeviceCode: {
    payload: { clientId: string };
    result: {
      userCode: string;
      verificationUri: string;
      expiresIn: number;
    };
  };
  onedriveDisconnect: { payload: void; result: { success: boolean } };
  /** Force a full re-walk of the OneDrive index (ignores the delta cursor). */
  onedriveReindex: { payload: void; result: { success: boolean } };
  // GitHub host-run device-code auth, same contract as OneDrive above: the
  // frontend passes the public client_id, the host polls GitHub and pushes a
  // `github-auth` event on completion. The token never leaves the host.
  githubStartDeviceCode: {
    payload: { clientId: string };
    result: {
      userCode: string;
      verificationUri: string;
      expiresIn: number;
    };
  };
  githubDisconnect: { payload: void; result: { success: boolean } };
  /** Who the stored GitHub token belongs to; null when not connected. */
  githubIdentity: {
    payload: void;
    result: { login: string; name: string; email: string } | null;
  };
  /** Writable repos for the picker, most-recently-pushed first. */
  githubListRepos: {
    payload: void;
    result: {
      fullName: string;
      cloneUrl: string;
      defaultBranch: string;
      private: boolean;
    }[];
  };
  githubListBranches: {
    payload: { fullName: string };
    result: string[];
  };
  // One-click Claude sign-in: start drives `claude auth login` on the host
  // and returns the OAuth URL to open; the pasted code goes back via
  // claudeLoginCode; the outcome arrives on the `claude-auth` event.
  claudeLoginStart: { payload: void; result: { url: string } };
  claudeLoginCode: {
    payload: { code: string };
    result: { success: boolean };
  };
  claudeLoginCancel: { payload: void; result: { success: boolean } };
  claudeLogout: { payload: void; result: { success: boolean } };
  // One-click Codex sign-in: start drives `codex login --device-auth` on the
  // host and returns the verification URL + one-time code to show. The CLI
  // polls OpenAI itself; the outcome arrives on the `codex-auth` event.
  codexLoginStart: { payload: void; result: { url: string; code: string } };
  codexLoginCancel: { payload: void; result: { success: boolean } };
  codexLogout: { payload: void; result: { success: boolean } };
  cardFinalize: {
    payload: {
      postId: string;
      apply: boolean;
      branchName?: string;
      commitMessage?: string;
    };
    result: { applied: boolean; branchName: string };
  };
  cardAnswerQuestion: {
    payload: { postId: string; answer: string };
    result: void;
  };
  cardFeedback: {
    payload: { postId: string; feedback: string };
    result: void;
  };
  cardKill: { payload: { postId: string }; result: void };
  /** Stop a card's headless agent but keep its worktree + conversation so a
   *  terminal can resume the exact thread (terminal takeover). */
  cardHandoff: { payload: { postId: string }; result: void };
  /** Stop the board-level Ask agent and return its resume id + provider, so the
   *  client opens a terminal (on the board key) that resumes the conversation. */
  boardHandoff: {
    /** Which conversation to hand over; none means the most recently used. */
    payload: { conversationId?: string };
    result: { sessionId: string | null; provider: AgentProvider };
  };

  // ── Bare mode: file ops in the card's workspace ──
  bareFsList: {
    payload: { postId: string; path: string };
    result: BareFileNode[];
  };
  bareFsRead: {
    payload: { postId: string; path: string };
    result: { content: string; encoding: 'utf8' | 'base64' };
  };
  bareFsWrite: {
    payload: {
      postId: string;
      path: string;
      content: string;
      encoding: 'utf8' | 'base64';
    };
    result: void;
  };
  bareFsDelete: { payload: { postId: string; path: string }; result: void };
  bareFsMkdir: { payload: { postId: string; path: string }; result: void };

  /** Drag-and-drop an image onto the terminal: writes the base64 image to the
   *  per-space drops dir and returns the absolute host path to paste.
   *
   *  Sent in chunks, because socket.io drops any single packet over 1MB and a
   *  retina screenshot is bigger than that. Every chunk carries the same
   *  `dropId` and appends to the same file; only the `last` one returns the
   *  path (earlier ones return ''). */
  terminalDropImage: {
    payload: {
      postId: string;
      content: string;
      ext?: string;
      /** Client-generated UUID naming the file this drop appends to. Absent
       *  from a client older than the chunked protocol, which sends the whole
       *  image in one call. */
      dropId?: string;
      /** Closes the drop: prunes old files and returns the path. */
      last?: boolean;
    };
    result: string;
  };

  /** All repo file paths (git-tracked + untracked, gitignore-respecting) for
   * the editor's quick file search. Paths are `/`-prefixed repo-relative. */
  bareFsSearch: {
    payload: { postId: string };
    result: string[];
  };

  /**
   * Snapshot the working tree's git diff against HEAD and move the session into
   * `bare-review`. Frees the user to confirm + push via `cardFinalize`.
   */
  bareReview: {
    payload: { postId: string };
    result: {
      diff: string;
      changedFiles: string[];
      commitMessage: string;
    };
  };

  /**
   * Lightweight peek at the workspace's git state. Does NOT change session
   * phase - used by the git side panel to live-poll while the user is still
   * editing. Covers all three things the panel shows: what is staged, what is
   * not, and what is already committed.
   */
  bareGitStatus: {
    payload: { postId: string };
    result: {
      /** Everything uncommitted (index + working tree) against HEAD. Kept
       *  because a mobile build older than the staged/unstaged split still
       *  reads it. */
      diff: string;
      changedFiles: string[];
      /** Everything below is absent when an older runner answers the call, so
       *  a new client stays readable against one. */
      branch?: string;
      staged?: { files: BareGitFileStatus[]; diff: string };
      unstaged?: { files: BareGitFileStatus[]; diff: string };
      /** Newest first. */
      commits?: BareGitCommit[];
    };
  };

  /** The patch one commit introduced, fetched when the panel expands it -
   *  history is cheap to list and expensive to diff, so the diff waits for a
   *  click. */
  bareGitCommitDiff: {
    payload: { postId: string; sha: string };
    result: { diff: string; changedFiles: string[] };
  };

  /**
   * Move paths into the index (`git add`). An empty `paths` stages everything,
   * which is what the section header's + does.
   *
   * Returns the panel's next state rather than void: staging touches only
   * .git/index, and the working-tree watcher prunes .git, so nothing would
   * tell the panel to re-read.
   */
  bareGitStage: {
    payload: { postId: string; paths: string[] };
    result: BoardAgentActionMap['bareGitStatus']['result'];
  };

  /** Take paths back out of the index. Empty `paths` unstages everything. */
  bareGitUnstage: {
    payload: { postId: string; paths: string[] };
    result: BoardAgentActionMap['bareGitStatus']['result'];
  };

  /**
   * List terminal tmux session tab IDs that exist for this card, paired with
   * the label the tab strip shows: the title the foreground program publishes
   * (`pane_title` - claude puts its current task there), falling back to the
   * process name (`pane_current_command` - `bash`, `vim`, ...) when it sets
   * none, so tabs read like VSCode's. Source of truth across modal
   * close/reopen.
   */
  bareListTerminals: {
    payload: { postId: string };
    result: {
      id: string;
      name: string;
      /** tmux is holding the pane open after its process exited
       *  (`remain-on-exit`), so the tab survived a stray `exit`. */
      dead: boolean;
      // No per-tab `stale`: it cost a digest read on the tab strip's poll to
      // draw a dot that was up on every tab. `bareTerminalHealth` answers
      // staleness for a whole board instead.
    }[];
  };

  /**
   * Whether this board's terminals still match what the runner ships. Answers
   * the "your update needs a restart" prompt: `staleTabs` is what a refresh
   * would restart, and `acked` is true once the user has said "skip for this
   * version" to this exact digest.
   *
   * Digest, not runner version: a release that changes nothing about terminals
   * asks nobody, and one that does is caught without anyone maintaining a list.
   */
  bareTerminalHealth: {
    payload: { postIds: string[] };
    result: {
      digest: string;
      acked: boolean;
      staleTabs: { postId: string; tabId: string }[];
    };
  };

  /** Record "skip for this version" for the digest the runner is on now, so
   *  the prompt stays down until something about terminals actually changes
   *  again. */
  bareAckTerminalDigest: {
    payload: Record<string, never>;
    result: void;
  };

  /** Restart every stale tab on these posts, keeping each tab. Returns how
   *  many were restarted so the client can say what happened. */
  bareRestartStaleTerminals: {
    payload: { postIds: string[] };
    result: { restarted: number };
  };

  /**
   * Snapshot of the runner-side IDE state: which files are open in the
   * editor tab bar and which of those have unsaved buffers. Used by the
   * client to hydrate on modal mount / browser refresh - the runner is the
   * source of truth so closing the modal doesn't lose tabs or edits.
   */
  bareEditorState: {
    payload: { postId: string };
    result: {
      openedFiles: string[];
      buffers: Record<string, string>;
    };
  };

  /** Add `path` to the runner's opened-files list (idempotent). */
  bareOpenFile: {
    payload: { postId: string; path: string };
    result: void;
  };

  /** Remove `path` from openedFiles and discard any buffered edits for it. */
  bareCloseFile: {
    payload: { postId: string; path: string };
    result: void;
  };

  /**
   * Write the editor's current buffer to the runner. Distinct from
   * `bareFsWrite` - this only holds unsaved edits in memory; the file on
   * disk isn't touched until the user explicitly saves.
   */
  bareBufferSet: {
    payload: { postId: string; path: string; content: string };
    result: void;
  };

  /**
   * Everything the card's terminals are running, with the ports each process
   * listens on. Polled while the task manager is open, so it stays a read: it
   * starts nothing and kills nothing.
   */
  bareListProcesses: {
    payload: { postId: string };
    result: BareProcess[];
  };

  /**
   * Ask one of this card's processes to stop (SIGTERM). Refuses any pid that
   * is not in the card's own tmux process tree, so the panel can never signal
   * something that belongs to the machine or to another card.
   */
  bareKillProcess: {
    payload: { postId: string; pid: number };
    result: void;
  };

  /**
   * Open a public tunnel to a local port via frpc (runs factiii-expose).
   * Returns the live tunnel incl. its public URL.
   */
  bareExpose: {
    payload: { postId: string; port: number; name: string };
    result: ExposedPort;
  };

  /** Tear down a named tunnel (runs factiii-unexpose). */
  bareUnexpose: {
    payload: { postId: string; name: string };
    result: void;
  };

  /** The session's currently-open port tunnels. */
  bareListExposed: {
    payload: { postId: string };
    result: ExposedPort[];
  };

  // ── Scratchpad: the space's display surface (see .specs/scratchpad.md) ──
  //
  // Space-scoped, not per session: a scratchpad outlives the terminal that
  // drew on it, and every session on the board looks at the same one.
  scratchpadList: { payload: Record<string, never>; result: ScratchpadItem[] };
  scratchpadRead: {
    payload: { name: string };
    result: { content: string; encoding: 'base64' };
  };
  /** One byte range, for downloading an item the preview cap refuses to
   *  inline. Reading a 200MB recording as a single base64 string would pin the
   *  channel and the browser's memory; a range at a time streams. */
  scratchpadReadChunk: {
    payload: { name: string; offset: number; length: number };
    result: { content: string; encoding: 'base64' };
  };
  scratchpadDelete: { payload: { name: string }; result: void };
  scratchpadClear: { payload: Record<string, never>; result: void };

  // ── Simulator: the space's iOS simulator panel ──
  //
  // Space-scoped like the scratchpad, and for the same reason: the simulator
  // belongs to the machine, not to whichever terminal happens to be open on it.

  simulatorList: { payload: Record<string, never>; result: SimulatorDevice[] };
  /**
   * Which simulators are booted.
   *
   * A set, not "the booted one": several can run at once, and the panel's
   * auto-open has to fire when a *new* one starts rather than when any is up.
   */
  simulatorStatus: {
    payload: Record<string, never>;
    result: { booted: string[] };
  };
  /** Start pushing frames. Re-attaching with the same options is a no-op; new
   *  options restart the encoder, whose output size is fixed at session start. */
  simulatorAttach: {
    payload: { udid: string; width: number; fps: number; bitrate: number };
    result: void;
  };
  simulatorDetach: { payload: Record<string, never>; result: void };
  simulatorInput: { payload: SimulatorInput; result: void };

  // ── Credential store (runner-wide, owner only) ──
  //
  // Not board-scoped: one vault protects every board on the machine, so a
  // member the runner is shared with can neither unlock it nor change the
  // password. The daemon enforces that before dispatch.

  /** Current protection state, including the fix text when it isn't usable. */
  secureStatus: { payload: void; result: SecureStoreStatus };
  /** Unlock a password-protected runner for the life of its process. */
  secureUnlock: { payload: { password: string }; result: SecureStoreStatus };
  /** Forget the key now: everything locks until the password is re-entered. */
  secureLock: { payload: void; result: SecureStoreStatus };
  /**
   * Add or change the optional owner password. `current` is required only when
   * one is already set. Re-wraps the data key; no credential is rewritten.
   */
  secureSetPassword: {
    payload: { current?: string; password: string };
    result: SecureStoreStatus;
  };
  /** Drop back to machine-keychain-only protection (still encrypted). */
  secureClearPassword: {
    payload: { password: string };
    result: SecureStoreStatus;
  };
};

export type BareFileNode = {
  name: string;
  /** Path relative to the repo root. */
  path: string;
  type: 'file' | 'dir';
};

export type ExposedPort = {
  name: string;
  port: number;
  url: string;
};

/** One process a card's terminals are running, as the task manager lists it.
 *  Everything a user starts in a tab is a descendant of that tab's shell, which
 *  is what ties a process to a card rather than to the machine. */
export type BareProcess = {
  pid: number;
  /** The tab it belongs to, numbered as bareListTerminals numbers them. */
  tabId: string;
  /** The binary, without its path: 'node', 'claude', 'vite'. */
  command: string;
  /** The full command line. */
  args: string;
  /** Percent of one core, as ps reports it. */
  cpu: number;
  /** Resident memory in KB. */
  memoryKb: number;
  /** How long it has been up: [[dd-]hh:]mm:ss. */
  elapsed: string;
  /** TCP ports it accepts connections on. Empty for most processes. */
  ports: number[];
  /** Connections currently established. The honest stand-in for bandwidth,
   *  which macOS will not report per-process without elevated privileges. */
  connections: number;
};

/**
 * A coalesced batch of working-tree changes made by someone other than the
 * editor - the card's agent, the terminal, or a git operation. Paths are
 * repo-relative and `/`-prefixed, same as `BareFileNode.path`.
 */
export type BareFsChange = {
  added: string[];
  changed: string[];
  removed: string[];
  /** Too many paths to be worth patching (a checkout, an install, a build):
   *  every list is empty and the client should refetch what it displays. */
  resync: boolean;
  /** The watch died under a live session (the OS ran out of watches). What's
   *  on screen is frozen from here, so the UI falls back to a manual refresh
   *  rather than pretending to be live. A session ENDING is silent - its
   *  session-update already says so. */
  stopped?: boolean;
};

export type BoardAgentAction = keyof BoardAgentActionMap;

/** Discriminated union - switch on `msg.action` narrows `msg.payload` automatically. */
export type BoardAgentMessage = {
  [A in BoardAgentAction]: {
    action: A;
    spaceSlug: string;
    // Stable board id (slugs can change).
    // Optional for back-compat with older clients that only send the slug;
    // the engine falls back to slug-based naming when absent.
    spaceId?: number;
    payload: BoardAgentActionMap[A]['payload'];
  };
}[BoardAgentAction];

export type BoardAgentEventMap = {
  log: CardAILogEntry;
  status: string;
  'setup-status': string;
  'session-update': CardAISessionSummary;
  'session-removed': { postId: string };
  // Space-level work that isn't a card session (Ask Board, deploy, setup,
  // indexing, sign-ins). `state: null` retires the row.
  'activity-update': RunnerActivity;
  // Pushed by the host when the device-code flow finishes (it polls Microsoft
  // itself, then alerts the client once - no client-side polling).
  'onedrive-auth': { status: OneDrivePollStatus; message?: string };
  // Pushed by the host when the GitHub device-code flow finishes. Carries the
  // resolved identity so the UI can show "Connected as @x" without a refetch.
  'github-auth': {
    status: 'complete' | 'expired' | 'declined' | 'error';
    message?: string;
    login?: string;
  };
  // Pushed by the host when the one-click Claude sign-in finishes (the host
  // drives the CLI itself; the client only opens the URL and relays the code).
  'claude-auth': { status: 'complete' | 'error'; message?: string };
  // Pushed by the host when the one-click Codex device-auth finishes (the CLI
  // polls OpenAI itself; the client only shows the URL + code).
  'codex-auth': { status: 'complete' | 'error'; message?: string };
  // Creator session settled (drafted or failed) - a reopened modal refetches
  // deployCreatorState on this signal.
  'deploy-creator': { phase: 'review' | 'error'; error?: string };
  // Deploy run transitioned; clients refetch deployRunState.
  'deploy-run': { phase: string };
  // Env check/fix settled; clients refetch deployEnvState.
  'deploy-env': { phase: 'done' | 'error' };
  // A command somewhere in this space is waiting on the owner's store
  // password, or has stopped waiting. Carries only whether something is
  // pending; the detail comes from secretsAuthState, so a stale broadcast
  // cannot leave a prompt on screen for an answered request.
  'secrets-auth': { pending: boolean };
  // Spotlight-style OneDrive index progress, pushed as a pass runs.
  'onedrive-index': {
    status: 'indexing' | 'idle' | 'error';
    done: number;
    error?: string;
  };
  // The space's scratchpad changed on disk. Carries no paths: the panel holds a
  // whole flat listing and refetching it is one small call.
  scratchpad: { at: number };
  [key: `log:${string}`]: CardAILogEntry;
  // Working-tree changes for one bare session, keyed by its postId so an open
  // modal only wakes for its own card.
  [key: `bare-fs:${string}`]: BareFsChange;
};

// Setup status lines share one channel (emitSetupStatus). A line carrying this
// prefix is streamed LOG output and accumulates in the setup log block; a plain
// line is the current step label and replaces the previous one. Same split the
// Docker image build used, when BuildKit's own "#" prefix did the marking.
export const SETUP_LOG_PREFIX = '\u0001log ';

// Initialize needs the owner's sudo password to install a system package (or
// to bootstrap Homebrew). A line with this prefix carries the reason and pauses
// setup until `setupAuthorize` answers. ONLY the runner's own toolchain
// commands ever ask: the agent protocol has no path to sudo, deliberately.
export const SETUP_AUTH_PREFIX = '\u0001sudo ';

// ── Bare mode: interactive shell into the card's workspace ──
// Multiplexed over the existing IPC/data-channel transport. Each terminal
// is identified by an opaque sessionId minted by the client; the runner
// does not need to remember it across reconnects.

export type TerminalClientMessage =
  | {
      type: 'terminal';
      op: 'open';
      sessionId: string;
      postId: string;
      cols: number;
      rows: number;
      /**
       * Stable tab key. Maps to a named tmux session, so
       * closing/reopening the modal reattaches to the same shell.
       */
      terminalKey: string;
      /**
       * When set, this tab boots straight into the card's agent conversation
       * (`claude --resume <id>` / `codex resume <id>`) instead of a bare
       * prompt. Only honoured on tmux session *creation* — reopening the
       * modal reattaches to the already-running agent rather than launching
       * a second one. Exiting the agent drops to a normal shell.
       */
      resumeAgentSessionId?: string;
      /** Which CLI owns `resumeAgentSessionId`. Defaults to claude. */
      resumeProvider?: AgentProvider;
      /**
       * This tab belongs to a card and should start a NEW agent pointed at it.
       * The card's text is never sent: the runner writes an opening prompt
       * naming the card's uuid, and the agent reads it through the
       * factiii-board MCP.
       *
       * Ignored when `resumeAgentSessionId` is set — a resumed thread already
       * knows the card — and, like resume, only honoured on tmux session
       * creation.
       */
      briefCard?: boolean;
    }
  | { type: 'terminal'; op: 'stdin'; sessionId: string; data: string }
  | {
      type: 'terminal';
      op: 'resize';
      sessionId: string;
      cols: number;
      rows: number;
    }
  | { type: 'terminal'; op: 'close'; sessionId: string }
  /** Destroy the underlying tmux session entirely (used when the user
   *  explicitly closes a tab; plain `close` only detaches). */
  | {
      type: 'terminal';
      op: 'kill';
      postId: string;
      terminalKey: string;
    }
  /** Kill what the tab is running and start a fresh shell in the SAME tmux
   *  session, so the tab keeps its place while the processes in it die.
   *  Unlike `kill`, the tab survives; unlike `close`, the shell does not. */
  | {
      type: 'terminal';
      op: 'restart';
      postId: string;
      terminalKey: string;
    };

export type TerminalServerMessage =
  | { type: 'terminal'; op: 'opened'; sessionId: string }
  | { type: 'terminal'; op: 'stdout'; sessionId: string; data: string }
  | {
      type: 'terminal';
      op: 'exit';
      sessionId: string;
      code: number | null;
      reason?: string;
    }
  | { type: 'terminal'; op: 'error'; sessionId: string; message: string };

/** The name the client opens its detached terminal window under. Shared: the
 *  desktop shell keys on it to tell an app window from a clicked link. */
export const TERMINAL_WINDOW_NAME = 'factiii-terminal';
